import type { ReactNode } from 'react'

import type { CotizacionWorldOffice } from '@/lib/supabase/tipos-bd'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { fecha as formatoFecha, pesos, porcentaje } from '@/shared/utilidades/formato'

import { useConceptos } from './useCotizaciones'
import estilos from './ConceptosCotizacion.module.css'

/** Cantidad sin decimales cuando es entera: "17" se lee mejor que "17,00". */
function cantidad(valor: number): string {
  return Number.isInteger(valor) ? String(valor) : valor.toFixed(2).replace('.', ',')
}

/**
 * Una cotización de World Office, abierta.
 *
 * Se dibuja como el documento que es —encabezado, conceptos y totales al
 * pie— y no como una tabla con una caja de cifras al lado.
 */
export function ConceptosCotizacion({
  cotizacion,
  acciones,
}: {
  cotizacion: CotizacionWorldOffice
  /**
   * Qué se puede hacer con este documento. Va en el encabezado, junto al
   * número: debajo de los conceptos habría que bajar veintiséis renglones
   * para encontrarlo, y un botón que hay que buscar es un botón que no está.
   */
  acciones?: ReactNode
}) {
  const { conceptos, cargando, error } = useConceptos(cotizacion.id_asiento)

  if (cargando) return <Cargando compacto mensaje="PROCESANDO CONCEPTOS COTIZACIÓN..." />

  if (error) {
    return (
      <div className={estilos.zona}>
        <Aviso tipo="error">{error}</Aviso>
      </div>
    )
  }

  if (conceptos.length === 0) {
    return (
      <div className={estilos.zona}>
        <Aviso tipo="alerta">
          Esta cotización llegó de World Office sin renglones de detalle.
        </Aviso>
      </div>
    )
  }

  const iva =
    cotizacion.total_con_iva !== null && cotizacion.base_sin_iva !== null
      ? cotizacion.total_con_iva - cotizacion.base_sin_iva
      : null

  // La tasa se lee del documento, no se asume el 19 %: puede haber conceptos
  // excluidos o con tarifa distinta.
  const tasa = conceptos.find((c) => c.iva_tasa)?.iva_tasa
  const rotuloIva = tasa ? `IVA ${Math.round(tasa * 100)} %` : 'IVA'

  return (
    <div className={estilos.zona}>
      <article className={estilos.hoja}>
        <header className={estilos.cabecera}>
          <div>
            <p className={estilos.numero}>{cotizacion.numero_visible}</p>
            <p className={estilos.cliente}>{cotizacion.cliente_nombre ?? 'Sin cliente'}</p>
          </div>

          <div className={estilos.meta}>
            <div>
              <p className={estilos.metaRotulo}>Fecha</p>
              <p className={estilos.metaValor}>{formatoFecha(cotizacion.fecha)}</p>
            </div>

            <div>
              <p className={estilos.metaRotulo}>Conceptos</p>
              <p className={estilos.metaValor}>{conceptos.length}</p>
            </div>

            {cotizacion.elaborada_por_world_office && (
              <div>
                <p className={estilos.metaRotulo}>Elaborada por</p>
                <p className={estilos.metaValor}>{cotizacion.elaborada_por_world_office}</p>
              </div>
            )}
          </div>

          {acciones && <div className={estilos.acciones}>{acciones}</div>}
        </header>

        <div className={estilos.desplazable}>
          <table className={estilos.tabla}>
            <thead>
              <tr>
                <th style={{ width: 38 }}>#</th>
                <th style={{ width: 92 }}>Código</th>
                <th>Concepto</th>
                <th className={estilos.derecha} style={{ width: 68 }}>
                  Cant.
                </th>
                <th className={estilos.derecha} style={{ width: 76 }}>
                  Unidad
                </th>
                <th className={estilos.derecha} style={{ width: 118 }}>
                  Unitario
                </th>
                <th className={estilos.derecha} style={{ width: 124 }}>
                  Subtotal
                </th>
              </tr>
            </thead>

            <tbody>
              {conceptos.map((concepto) => (
                <tr
                  key={concepto.linea}
                  className={concepto.es_mano_de_obra ? estilos.manoDeObra : undefined}
                >
                  <td className={estilos.indice}>{concepto.linea}</td>
                  <td className={estilos.codigo}>{concepto.codigo ?? '—'}</td>
                  <td className={estilos.nombreConcepto}>
                    {concepto.concepto}
                    {concepto.es_mano_de_obra && (
                      <span className={estilos.etiquetaMo}>Margen</span>
                    )}
                  </td>
                  <td className={`${estilos.derecha} ${estilos.cifra}`}>
                    {cantidad(concepto.cantidad)}
                  </td>
                  <td className={estilos.derecha}>{concepto.unidad ?? '—'}</td>
                  <td className={`${estilos.derecha} ${estilos.cifra}`}>
                    {pesos(concepto.precio_unitario)}
                  </td>
                  <td className={`${estilos.derecha} ${estilos.cifra}`}>
                    {pesos(concepto.subtotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <footer className={estilos.pie}>
          <div className={estilos.totales}>
            {/* Materiales y mano de obra solo aparecen cuando la cotización
                los separa. Una fila rotulada con el valor en blanco no
                informa de nada y ensucia el bloque. */}
            {cotizacion.discrimina_mano_de_obra && (
              <>
                <div className={estilos.fila}>
                  <span>Materiales</span>
                  <span>{pesos(cotizacion.materiales)}</span>
                </div>

                <div className={estilos.fila}>
                  <span>Mano de obra</span>
                  <span>{pesos(cotizacion.mano_de_obra)}</span>
                </div>
              </>
            )}

            <div className={`${estilos.fila} ${estilos.base}`}>
              <span>
                Base
                {cotizacion.porcentaje_mano_de_obra !== null && (
                  <span className={estilos.etiquetaMo}>
                    {porcentaje(cotizacion.porcentaje_mano_de_obra)} mano de obra
                  </span>
                )}
              </span>
              <span>{pesos(cotizacion.base_sin_iva)}</span>
            </div>

            <div className={estilos.fila}>
              <span>{rotuloIva}</span>
              <span>{pesos(iva)}</span>
            </div>

            <div className={`${estilos.fila} ${estilos.total}`}>
              <span>Total</span>
              <span>{pesos(cotizacion.total_con_iva)}</span>
            </div>

            {/* Sin la línea de mano de obra el margen sale de un porcentaje
                supuesto. Decirlo evita que se lea como medido algo estimado. */}
            {!cotizacion.discrimina_mano_de_obra && (
              <p className={estilos.aviso}>
                Esta cotización no separa la mano de obra, así que el margen saldrá del porcentaje
                configurado en el servicio y no de un dato real.
              </p>
            )}
          </div>
        </footer>
      </article>
    </div>
  )
}
